// pages/admin/UsersPage.tsx
/**
 * @description User management page for super admins
 */
import { useState, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Shield,
  Building,
  Mail,
  User,
  Search,
  ChevronDown,
  Loader2
} from 'lucide-react';
import { AdminLayout } from '../../components/layout';
import { Button, Modal, Input, Select, Badge } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import { getUsers, getBuildings, createUser, updateUser, deleteUser, type User as ApiUser, type Building as ApiBuilding } from '../../lib/api';

export function UsersPage() {
  const { showToast: addToast } = useToast();
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [buildings, setBuildings] = useState<ApiBuilding[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'super_admin' | 'leasing_agent' | 'all'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ApiUser | null>(null);

  // Fetch users and buildings from API
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [usersResult, buildingsResult] = await Promise.all([
          getUsers(),
          getBuildings()
        ]);

        if (usersResult.data?.users) {
          setUsers(usersResult.data.users);
        }
        if (buildingsResult.data?.buildings) {
          setBuildings(buildingsResult.data.buildings);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Filter users
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      (user.fullName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const handleAddUser = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: ApiUser) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (confirm('Are you sure you want to delete this user?')) {
      const result = await deleteUser(userId);
      if (result.error) {
        addToast(result.error, 'error');
      } else {
        setUsers((prev) => prev.filter((u) => u.id !== userId));
        addToast('User deleted successfully', 'success');
      }
    }
  };

  const handleSaveUser = async (formData: { fullName: string; email: string; role: string; assignedBuildings: string[] }) => {
    setSaving(true);
    try {
      if (editingUser) {
        // Update existing user
        const result = await updateUser(editingUser.id, formData);
        if (result.error) {
          addToast(result.error, 'error');
          return;
        }
        // Update local state
        setUsers((prev) =>
          prev.map((u) =>
            u.id === editingUser.id
              ? {
                  ...u,
                  fullName: formData.fullName,
                  email: formData.email,
                  role: formData.role as 'super_admin' | 'leasing_agent',
                  assignedBuildings: buildings
                    .filter((b) => formData.assignedBuildings.includes(b.id))
                    .map((b) => ({ id: b.id, name: b.name })),
                }
              : u
          )
        );
        addToast('User updated successfully', 'success');
      } else {
        // Create new user
        const result = await createUser(formData);
        if (result.error) {
          addToast(result.error, 'error');
          return;
        }
        // Add to local state
        const newUser: ApiUser = {
          id: result.data!.id,
          email: formData.email,
          fullName: formData.fullName,
          role: formData.role as 'super_admin' | 'leasing_agent',
          assignedBuildings: buildings
            .filter((b) => formData.assignedBuildings.includes(b.id))
            .map((b) => ({ id: b.id, name: b.name })),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        setUsers((prev) => [...prev, newUser]);
        addToast('User created successfully', 'success');
      }
      setIsModalOpen(false);
      setEditingUser(null);
    } finally {
      setSaving(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-3 text-gray-600">Loading users...</span>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Page Header - Stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 lg:mb-8">
          <div>
            <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">
              User Management
            </h1>
            <p className="text-sm lg:text-base text-gray-500 mt-1">
              Manage team members and their building access
            </p>
          </div>
          <Button onClick={handleAddUser} className="w-full sm:w-auto">
            <Plus size={16} className="mr-2" />
            Add User
          </Button>
        </div>

        {/* Filters - Stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 lg:gap-4 mb-4 lg:mb-6">
          <div className="relative flex-1 sm:max-w-sm">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 hidden sm:inline">Role:</span>
            <div className="relative flex-1 sm:flex-none">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as 'super_admin' | 'leasing_agent' | 'all')}
                className="w-full sm:w-auto appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2.5 pr-8 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="all">All Roles</option>
                <option value="super_admin">Super Admin</option>
                <option value="leasing_agent">Leasing Agent</option>
              </select>
              <ChevronDown
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
          </div>
        </div>

        {/* Users Table - Desktop */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned Buildings
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User size={20} className="text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {user.fullName || 'No name'}
                          </p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.role === 'super_admin' ? (
                        <Badge variant="warning">
                          <Shield size={12} className="mr-1" />
                          Super Admin
                        </Badge>
                      ) : (
                        <Badge>
                          <Building size={12} className="mr-1" />
                          Leasing Agent
                        </Badge>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {user.role === 'super_admin' ? (
                        <span className="text-sm text-gray-500 italic">
                          All buildings
                        </span>
                      ) : user.assignedBuildings.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {user.assignedBuildings.map((building) => (
                            <span
                              key={building.id}
                              className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded"
                            >
                              {building.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">
                          No buildings assigned
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleEditUser(user)}
                        className="text-primary hover:text-primary-hover p-1 mr-2"
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile User Cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {filteredUsers.map((user) => (
              <div key={user.id} className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <User size={20} className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{user.fullName || 'No name'}</p>
                    <p className="text-sm text-gray-500 truncate">{user.email}</p>
                    <div className="mt-2">
                      {user.role === 'super_admin' ? (
                        <Badge variant="warning">
                          <Shield size={12} className="mr-1" />
                          Super Admin
                        </Badge>
                      ) : (
                        <Badge>
                          <Building size={12} className="mr-1" />
                          Leasing Agent
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className="ml-13 space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Building size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      {user.role === 'super_admin' ? (
                        <span className="text-gray-500 italic">All buildings</span>
                      ) : user.assignedBuildings.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {user.assignedBuildings.map((building) => (
                            <span
                              key={building.id}
                              className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded"
                            >
                              {building.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">No buildings assigned</span>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-500">
                    Created: {user.createdAt
                      ? new Date(user.createdAt).toLocaleDateString()
                      : '-'}
                  </p>
                </div>

                <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleEditUser(user)}
                    className="flex-1 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    className="flex-1 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="px-4 lg:px-6 py-3 border-t border-gray-100 text-sm text-gray-500">
            Showing {filteredUsers.length} of {users.length} users
          </div>
        </div>
      </div>

      {/* Add/Edit User Modal */}
      <UserFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingUser(null);
        }}
        onSave={handleSaveUser}
        user={editingUser}
        buildings={buildings}
        saving={saving}
      />
    </AdminLayout>
  );
}

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { fullName: string; email: string; role: string; assignedBuildings: string[] }) => void;
  user: ApiUser | null;
  buildings: ApiBuilding[];
  saving: boolean;
}

function UserFormModal({
  isOpen,
  onClose,
  onSave,
  user,
  buildings,
  saving
}: UserFormModalProps) {
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    role: user?.role || 'leasing_agent',
    assignedBuildings: user?.assignedBuildings?.map(b => b.id) || []
  });

  // Reset form when modal opens or user changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        fullName: user?.fullName || '',
        email: user?.email || '',
        role: user?.role || 'leasing_agent',
        assignedBuildings: user?.assignedBuildings?.map(b => b.id) || []
      });
    }
  }, [isOpen, user]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const toggleBuilding = (buildingId: string) => {
    setFormData((prev) => ({
      ...prev,
      assignedBuildings: prev.assignedBuildings.includes(buildingId)
        ? prev.assignedBuildings.filter((id) => id !== buildingId)
        : [...prev.assignedBuildings, buildingId]
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={user ? 'Edit User' : 'Add New User'}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Saving...
              </>
            ) : user ? 'Save Changes' : 'Add User'}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Full Name"
          placeholder="John Smith"
          value={formData.fullName}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, fullName: e.target.value }))
          }
          required
        />

        <Input
          label="Email Address"
          type="email"
          placeholder="john@beauxproperties.ca"
          value={formData.email}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, email: e.target.value }))
          }
          required
        />

        <Select
          label="Role"
          value={formData.role}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              role: e.target.value as 'super_admin' | 'leasing_agent'
            }))
          }
          options={[
            { value: 'leasing_agent', label: 'Leasing Agent' },
            { value: 'super_admin', label: 'Super Admin' }
          ]}
        />

        {formData.role === 'leasing_agent' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assigned Buildings
            </label>
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
              {buildings.map((building) => (
                <label
                  key={building.id}
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={formData.assignedBuildings.includes(building.id)}
                    onChange={() => toggleBuilding(building.id)}
                    className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <div className="flex items-center gap-2">
                    <Building size={16} className="text-gray-400" />
                    <span className="text-gray-900">{building.name}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {!user && (
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Mail size={16} className="text-blue-600" />
              <p className="text-sm text-blue-700">
                An invitation email will be sent to the user.
              </p>
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}
